/**
 * Providers, in the order they depend on each other:
 * query -> theme -> session -> event stream -> toasts -> routes.
 *
 * The stream sits inside the session (it needs to know somebody is signed in before it opens a
 * socket) and outside the routes (one socket for the whole console, not one per page).
 *
 * Author: Danish Khan
 */
import * as Tooltip from '@radix-ui/react-tooltip';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router';
import type { ReactNode } from 'react';
import { EventStreamProvider } from './lib/events.tsx';
import { SessionProvider } from './lib/session.tsx';
import { ThemeProvider } from './lib/themeProvider.tsx';
import { ToastProvider } from './components/Toast.tsx';
import { AppRoutes } from './router.tsx';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 10_000,
    },
  },
});

export function App(): ReactNode {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <Tooltip.Provider delayDuration={150} skipDelayDuration={300}>
          <SessionProvider>
            <EventStreamProvider>
              <ToastProvider>
                <BrowserRouter>
                  <AppRoutes />
                </BrowserRouter>
              </ToastProvider>
            </EventStreamProvider>
          </SessionProvider>
        </Tooltip.Provider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
